import React from "react";
import { VisuallyHidden } from "react-aria-components";

import {
    MediaCardBlock as CardValue,
    GoodcookMediaChooserBlockOrNull,
    SimpleLinkBlock,
    MediaCardsBlock as Value,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Carousel } from "../GoodcookCarousel";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./MediaCardsBlock.module.scss";

export interface Props {
    value: Value;
}

const getLinkText = (
    link: SimpleLinkBlock,
    media: GoodcookMediaChooserBlockOrNull,
) => {
    if (link.display_name) return link.display_name;
    if (media?.[0]?.type === "image") return media[0].value.description;
    if (media?.[0]?.type === "video" && media[0].value.video)
        return media[0].value.video.title;
    // TODO something better than this
    return link.url;
};

function Card({ media, link, text }: CardValue) {
    const textHasLink = text?.content?.includes("<a ") || false;

    return (
        <div className={styles.card}>
            {link?.url && (
                <Clickable simpleLink={link} className={styles.clicky}>
                    <VisuallyHidden>
                        {getLinkText(link, media) || text?.content}
                    </VisuallyHidden>
                </Clickable>
            )}
            {media && <MediaChooserBlock value={media} />}
            {text?.content && (
                <RichText
                    value={text.content}
                    className={concatClassNames([
                        styles.richText,
                        textHasLink ? styles.richTextOnTop : "",
                    ])}
                    textAlignment={
                        text.alignment === "textAlignCenter" ? "center" : "left"
                    }
                />
            )}
        </div>
    );
}

function groupBy<T>(list: T[], count: number) {
    return list.reduce((acc, card, index) => {
        const page = Math.floor(index / count);
        acc[page] = acc[page] || [];
        acc[page].push(card);
        return acc;
    }, [] as T[][]);
}

export function MediaCardsBlock({ value: { cards, behavior } }: Props) {
    if (!cards) return null;
    behavior = behavior ?? "four_up_wrap";
    const cardsPerPage = behavior === "three_up_wrap" ? 3 : 4;

    let content: React.ReactNode;
    if (behavior === "four_up_overflow") {
        content = cards.map((card, index) => (
            <Card key={`${card.id}-${index}`} {...card.value} />
        ));
    } else {
        /**
         * If we aren't overflowing we need to divide the cards into pages so they can wrap on mobile
         */

        const pages = groupBy(cards, cardsPerPage);
        content = pages.map((page, index) => (
            <div key={index} className={styles.page}>
                {page.map((card, index) => (
                    <Card key={`${card.id}-${index}`} {...card.value} />
                ))}
            </div>
        ));
    }

    return (
        <Carousel
            label="Media cards"
            containerProps={{ className: styles.root }}
            scrollContainerProps={{
                className: concatClassNames([
                    styles[behavior],
                    cards.length < cardsPerPage ? styles.unfilledCarousel : "",
                ]),
            }}
        >
            {content}
        </Carousel>
    );
}
